import React, { useEffect } from 'react';
import Handlebars from 'handlebars';
import styles from './PreviewRenderer.module.scss';
import LoadingSpinner from '../../_custom/LoadingSpinner/LoadingSpinner';
import { downloadIconDataRoomHovered } from '../../../assets/hostedassets';
import { downloadFile } from '../../../modules/Company/UploadJourney/components/UploadCard/utils/api/helpers';

interface PreviewData {
    templateHTML: string;
    toField: string;
    subject: string;
    templateValues: any;
    filesMetaData: { docName: string; id: number }[];
}

const PreviewRenderer = () => {
    const [previewData, setPreviewData] = React.useState<PreviewData | null>(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [downloadingFiles, setDownloadingFiles] = React.useState<Set<number>>(new Set());

    // Set body background color when component mounts
    useEffect(() => {
        const originalBackground = document.body.style.background;
        document.body.style.background = '#979797';

        // Cleanup function to restore original background when component unmounts
        return () => {
            document.body.style.background = originalBackground;
        };
    }, []);

    // Get the preview data from the session storage
    useEffect(() => {
        const storedData = sessionStorage.getItem('emailPreviewData');
        if (storedData) {
            try {
                const parsedData = JSON.parse(storedData);
                setPreviewData(parsedData);
                setIsLoading(false);
            } catch (error) {
                console.error('Error parsing sessionStorage data:', error);
                setIsLoading(false);
            }
        } else {
            setIsLoading(false);
        }

        return () => {
            sessionStorage.removeItem('emailPreviewData');
        };
    }, []);

    // Compile the received template HTML with the template values
    const compiledBody = React.useMemo(() => {
        if (!previewData) return '';

        try {
            const template = Handlebars.compile(previewData?.templateHTML);
            return template(previewData?.templateValues);
        } catch (error) {
            console.error('Error compiling template:', error);
            return '';
        }
    }, [previewData]);

    if (isLoading) {
        return (
            <div className={styles.emailPreviewContainer}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <LoadingSpinner height="14px" color="#6021B3" />
                </div>
            </div>
        );
    }

    if (!previewData) {
        return (
            <div className={styles.emailPreviewContainer}>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    No preview data available
                </div>
            </div>
        );
    }

    const downloadFunction = async (file) => {
        setDownloadingFiles((prev) => new Set(prev).add(file?.id));

        try {
            await downloadFile(
                file.id,
                () => {
                    // Success callback
                    setDownloadingFiles((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(file?.id);
                        return newSet;
                    });
                },
                file.docName,
                () => {
                    // Error callback
                    setDownloadingFiles((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(file?.id);
                        return newSet;
                    });
                },
            );
        } catch (error) {
            console.error('Download error:', error);
            setDownloadingFiles((prev) => {
                const newSet = new Set(prev);
                newSet.delete(file?.id);
                return newSet;
            });
        }
    };

    return (
        <div className={styles.emailPreviewContainer}>
            <div className={styles.header}>
                <div className={styles.headerRow}>
                    <span className={styles.label}>To:</span>
                    <span className={styles.value}>{previewData.toField}</span>
                </div>
                <div className={styles.headerRow}>
                    <span className={styles.label}>Subject:</span>
                    <span className={styles.value}>{previewData.subject}</span>
                </div>
            </div>

            <div className={styles.bodyContent}>
                <div className={styles.templateContent} dangerouslySetInnerHTML={{ __html: compiledBody }} />
            </div>

            {previewData.filesMetaData.length > 0 && (
                <div className={styles.footer}>
                    <div className={styles.attachmentsContainer}>
                        {previewData.filesMetaData.map((file, index) => {
                            const isDownloading = downloadingFiles.has(file.id);
                            return (
                                <div
                                    key={index}
                                    className={styles.attachmentPillContainer}
                                    onClick={() => !isDownloading && downloadFunction(file)}
                                    style={{ cursor: isDownloading ? 'not-allowed' : 'pointer' }}
                                >
                                    <span className={styles.downloadIcon}>
                                        {isDownloading ? (
                                            <LoadingSpinner height="14px" color="#6021B3" style={{ margin: '0px' }} />
                                        ) : (
                                            <img src={downloadIconDataRoomHovered} alt="download" />
                                        )}
                                    </span>
                                    <span className={styles.attachmentPill}>{file.docName}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PreviewRenderer;
